import * as webllm from "@mlc-ai/web-llm";
import { DEFAULT_SLM_MODEL } from "./models";

// ponytail: model download backend. Two problems drove this choice:
//  - "cache" (default): stores with Cache.add(), which is stricter than plain
//    fetch and throws "Cache.add() encountered a network error" on HF's Xet CDN
//    (us.aws.cdn.hf.co) responses.
//  - "indexeddb": works around that but buffers each whole file via
//    response.arrayBuffer() before storing -> "Array buffer allocation failed"
//    (JS-heap OOM) on big shards / low-RAM machines.
// "opfs" streams response.body.pipeTo(writable) straight to disk: no giant
// ArrayBuffer, no Cache.add(). Needs OPFS (Chrome 102+, present wherever WebGPU is).
const SLM_APP_CONFIG = { ...webllm.prebuiltAppConfig, cacheBackend: "opfs" };

// Single long-lived engine = single WebGPU device. Switching models MUST reuse
// it via engine.reload() (which unloads the old model on the same device);
// creating a fresh CreateMLCEngine per model spins a new device and leaks the
// old model's VRAM -> OOM / device-lost when switching (e.g. 0.5B then 1.5B).
let engine = null;
let loadedModel = null;
let opQueue = Promise.resolve(); // serialize load/reload so they never overlap

export function isWebGPUAvailable() {
  return typeof navigator !== "undefined" && !!navigator.gpu;
}

// Real availability check: the WebGPU API can exist while no usable GPU
// adapter is present (e.g. no discrete/integrated GPU, or disabled). Only
// a non-null adapter means WebLLM can actually run.
export async function checkWebGPU() {
  if (typeof navigator === "undefined" || !navigator.gpu) {
    console.warn("[WebGPU] navigator.gpu is missing — WebGPU off or unsupported browser.");
    return false;
  }
  try {
    // Prefer the discrete GPU; fall back to default selection.
    let adapter = await navigator.gpu.requestAdapter({ powerPreference: "high-performance" });
    if (!adapter) adapter = await navigator.gpu.requestAdapter();
    if (!adapter) {
      console.warn(
        "[WebGPU] navigator.gpu exists but requestAdapter() returned null.\n" +
        "Likely causes: hardware acceleration disabled in the browser, GPU on the " +
        "blocklist, or running inside an embedded webview without GPU access.\n" +
        "Check chrome://gpu (look for the 'WebGPU' line) and https://webgpureport.org/"
      );
      return false;
    }
    console.info("[WebGPU] adapter acquired OK.");
    return true;
  } catch (e) {
    console.warn("[WebGPU] requestAdapter threw:", e);
    return false;
  }
}

/**
 * Lazily create (and cache) the WebLLM engine for the given model.
 * Re-initialises only when the model id changes. The first call downloads
 * the quantized weights (~1-2GB) and caches them in the browser.
 */
export function initEngine(modelId = DEFAULT_SLM_MODEL, onProgress) {
  // Chain onto the queue so two switches can't reload the same device at once.
  const run = opQueue.then(() => loadModel(modelId, onProgress));
  // Keep the queue alive even if this op rejects (a failure must not wedge it).
  opQueue = run.catch(() => {});
  return run;
}

async function loadModel(modelId, onProgress) {
  if (engine && loadedModel === modelId) return engine;
  const cb = (report) => { if (onProgress) onProgress(report); };
  try {
    if (!engine) {
      engine = await webllm.CreateMLCEngine(modelId, { appConfig: SLM_APP_CONFIG, initProgressCallback: cb });
    } else {
      // Reuse the existing device; reload() unloads the current model first.
      engine.setInitProgressCallback(cb);
      await engine.reload(modelId);
    }
    loadedModel = modelId;
    return engine;
  } catch (err) {
    // Failed load (OOM) or a lost device leaves the engine unusable — tear it
    // down so the next attempt rebuilds a fresh device instead of reusing a
    // dead one. Reset so the next call retries cleanly.
    try { if (engine && typeof engine.unload === "function") await engine.unload(); } catch (e) { /* ignore */ }
    engine = null;
    loadedModel = null;
    throw err;
  }
}

/**
 * Drop the live engine reference without touching cached model files. A
 * failed *initial* load already resets this in loadModel's own catch, but a
 * device lost mid-generation (GPU driver reset, another tab claiming the
 * GPU) throws from generateJSON instead, which never resets this module's
 * state — the next call would see `loadedModel === modelId` and reuse the
 * now-dead engine, failing identically forever. Call this whenever a
 * generation-time GPU/adapter error is detected, so the next attempt
 * rebuilds a fresh device instead of reusing a zombie reference.
 */
export async function resetEngine() {
  try {
    if (engine && typeof engine.unload === "function") await engine.unload();
  } catch (e) {
    /* ignore */
  }
  engine = null;
  loadedModel = null;
}

/**
 * Remove a model's cached weights/config. Used to recover from a corrupt or
 * interrupted download ("Cache.add() encountered a network error").
 */
export async function clearModelCache(modelId = DEFAULT_SLM_MODEL) {
  // Unload the live engine to release the GPU device before clearing caches.
  try {
    if (engine && typeof engine.unload === "function") await engine.unload();
  } catch (e) {
    /* ignore */
  }
  engine = null;
  loadedModel = null;
  try {
    if (typeof webllm.deleteModelAllInfoInCache === "function") {
      await webllm.deleteModelAllInfoInCache(modelId);
      return;
    }
  } catch (e) {
    console.warn("deleteModelAllInfoInCache failed, falling back:", e);
  }
  // Fallback: drop every WebLLM-owned Cache Storage bucket.
  if (typeof caches !== "undefined") {
    const keys = await caches.keys();
    await Promise.all(
      keys.filter((k) => k.toLowerCase().includes("webllm")).map((k) => caches.delete(k))
    );
  }
}

/**
 * Run a chat completion and return the raw text. Uses JSON mode so the SLM
 * is constrained to emit a single valid JSON object.
 */
export async function generateJSON(messages, { temperature = 0.4, maxTokens = 1024, schema } = {}) {
  if (!engine) throw new Error("SLM engine not initialised");
  const reply = await engine.chat.completions.create({
    messages,
    temperature,
    max_tokens: maxTokens,
    // ponytail: this WebLLM version routes json_object through compileJSONSchema
    // and passes response_format.schema straight to a WASM std::string binding.
    // Omit schema and it gets undefined -> "Cannot pass non-string to std::string".
    // A strict schema (passed by the caller) also grammar-constrains decoding so a
    // weak model can't degenerate into an unclosed / infinitely-repeating array.
    response_format: { type: "json_object", schema: schema || '{"type":"object"}' },
  });
  return reply.choices?.[0]?.message?.content || "";
}
